import {
  type EmployersEmployerInvoice,
  type EmployersMemberInvoiceDetail,
  type EmployersStripeInvoice,
  type Money,
} from './web_api.schemas'
import { convertGrapeMoneyToNumber, formatMoney } from '../utils'

export const INVOICE_TYPE = {
  EMPLOYER: 'employer',
  STRIPE: 'stripe',
}

export type InvoiceType = typeof INVOICE_TYPE[keyof typeof INVOICE_TYPE]

export const BILL_TYPE = {
  HEALTHCARE_COSTS: 'healthcare_costs',
  PLATFORM_FEES: 'platform_fees',
}

export type BillType = typeof BILL_TYPE[keyof typeof BILL_TYPE]

export const CURRENCY = {
  USD: 'USD',
}

export type CombinedInvoice =
  | EmployersEmployerInvoice
  | EmployersStripeInvoice

export function isStripeInvoice (invoice: CombinedInvoice): invoice is EmployersStripeInvoice {
  return invoice.type === INVOICE_TYPE.STRIPE
}

export function isEmployerInvoice (invoice: CombinedInvoice): invoice is EmployersEmployerInvoice {
  return invoice.type === INVOICE_TYPE.EMPLOYER
}

export function isStripeInvoiceType (invoiceType: InvoiceType | null | undefined): boolean {
  return invoiceType === INVOICE_TYPE.STRIPE
}

export function formatInvoiceTitle (invoice: CombinedInvoice): string {
  if (isStripeInvoice(invoice)) {
    const date = new Date(invoice.created)
    const monthName = date.toLocaleString('en-US', { month: 'long' })
    return `${monthName} platform fees`
  }
  if (invoice.title === 'Current') {
    return 'Current'
  }
  const date = new Date(invoice.period_end_date)
  const monthName = date.toLocaleString('en-US', { month: 'long' })
  return `${monthName} healthcare costs`
}

export function getInvoiceDate (invoice: CombinedInvoice): Date {
  if (isStripeInvoice(invoice)) {
    return new Date(invoice.created)
  }
  if (!invoice.period_end_date) {
    return new Date()
  }
  return new Date(invoice.period_end_date)
}

export function getInvoiceNumber (invoice: CombinedInvoice): string | null {
  if (isStripeInvoice(invoice)) {
    return invoice.invoice_number || invoice.id || null
  }
  const employerInvoice = invoice as EmployersEmployerInvoice & { type: string }
  return employerInvoice.invoice_number || null
}

export function getDefaultYear (
  availableYears: number[],
  currentYear: number,
  previousYear: number
): number {
  if (availableYears.length === 0) {
    return previousYear
  }
  if (availableYears.includes(currentYear)) {
    return currentYear
  }
  return availableYears[0]
}

export function formatInvoiceDate (date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(dateObj)
}

export function formatPeriodDate (dateString: string): string {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

export function formatMonthName (dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString('en-US', { month: 'long' })
}

export function getNextMonthName (dateString: string): string {
  const date = new Date(dateString)
  const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1)
  return nextMonth.toLocaleString('en-US', { month: 'long' })
}

export function getPriorMonthName (dateString: string): string {
  const date = new Date(dateString)
  const priorMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1)
  return priorMonth.toLocaleString('en-US', { month: 'long' })
}

export function formatMonthDateRange (dateString: string): string {
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = date.toLocaleString('en-US', { month: 'long' })
  const daysInMonth = new Date(year, date.getMonth() + 1, 0).getDate()
  return `${month} 1 – ${daysInMonth}`
}

export function formatNextMonthDateRange (dateString: string): string {
  const date = new Date(dateString)
  const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1)
  const year = nextMonth.getFullYear()
  const month = nextMonth.toLocaleString('en-US', { month: 'long' })
  const daysInMonth = new Date(year, nextMonth.getMonth() + 1, 0).getDate()
  return `${month} 1 – ${daysInMonth}`
}

export function getPriorMonthEnrollmentCutoffDate (periodStartDate: string): string {
  const periodStart = new Date(periodStartDate)
  const priorMonth = new Date(periodStart.getFullYear(), periodStart.getMonth() - 1, 14)
  return formatPeriodDate(priorMonth.toISOString())
}

export function countEmployeesWithPrefunding (memberInvoices: EmployersMemberInvoiceDetail[]): number {
  return memberInvoices.filter((mi) => {
    const scheduledPayments = convertGrapeMoneyToNumber(mi.amounts?.total_insurance_premiums)
    return scheduledPayments > 0
  }).length
}

export function countEmployeesWithPremiums (memberInvoices: EmployersMemberInvoiceDetail[]): number {
  return memberInvoices.filter((mi) => {
    const medical = convertGrapeMoneyToNumber(mi.medical_premiums_total)
    const dental = convertGrapeMoneyToNumber(mi.dental_premiums_total)
    const vision = convertGrapeMoneyToNumber(mi.vision_premiums_total)
    const premiumReimbursement = convertGrapeMoneyToNumber(mi.pretax_employer_premium_reimbursement)
    const premiumOverage = convertGrapeMoneyToNumber(mi.pretax_employee_medical_premium_overage)
    const posttaxOverage = convertGrapeMoneyToNumber(mi.posttax_employee_premium_overage)
    return medical > 0 || dental > 0 || vision > 0 || premiumReimbursement > 0 || premiumOverage > 0 || posttaxOverage > 0
  }).length
}

export function countEmployeesWithCoverageCorrections (memberInvoices: EmployersMemberInvoiceDetail[]): number {
  return memberInvoices.filter((mi) => {
    const corrections = convertGrapeMoneyToNumber(mi.amounts?.coverage_corrections)
    return corrections > 0
  }).length
}

export function countEmployeesWithQME (memberInvoices: EmployersMemberInvoiceDetail[]): number {
  return memberInvoices.filter((mi) => {
    const qmeReimbursement = convertGrapeMoneyToNumber(mi.pretax_employer_qme_reimbursement)
    const qmeOverage = convertGrapeMoneyToNumber(mi.posttax_employee_qme_overage)
    const other = convertGrapeMoneyToNumber(mi.amounts?.other)
    return qmeReimbursement > 0 || qmeOverage > 0 || other > 0
  }).length
}

export function countEmployeesWithDoubleBilling (memberInvoices: EmployersMemberInvoiceDetail[]): number {
  return memberInvoices.filter((mi) => {
    const doubleBilling = convertGrapeMoneyToNumber(mi.amounts?.double_billing_total)
    return doubleBilling > 0
  }).length
}

export function formatMoneyValue (money: Money | undefined): string {
  const value = convertGrapeMoneyToNumber(money)
  if (value === 0) {
    return '$0.00'
  }
  return formatMoney(value)
}

export function getFirstName (fullName: string | undefined): string {
  if (!fullName) return ''
  return fullName.split(' ')[0] || fullName
}
